import { Dayjs } from 'dayjs';
import { Country } from 'react-phone-number-input';
import type { EmailDomainSuggestion } from '../../../lib/emailDomainSuggestion.ts';
import {
  AvailabilityOption,
  PaymentType,
  MetaAvailability,
  Post_Confirm,
  Post_Reserve,
  RestaurantResponseData,
  EventReservationPayload,
} from '../../../lib/types.ts';

export const WIDGET_STATE_VERSION = '20260824.1200';

export interface WidgetState {
  club_checkout?: {
    fingerprint: string;
    key: string;
    body: EventReservationPayload;
    restaurantName: string;
    totalLabel: string;
  };
  version: string;
  expires: Dayjs;
  date?: string;
  /** Restaurant ID the current classic date belongs to (detects restaurant switches) */
  date_restaurant_id?: number;
  /** True when the venue map loaded as an interactive SVG; shared so the payment page can gate "Minimum Spend Deposit" copy */
  venue_map_interactive?: boolean;
  availability?: MetaAvailability;
  option?: AvailabilityOption;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_country?: Country;
  phone_number?: string;
  phone_confirmed: boolean | null;
  policy_accepted: boolean | null;
  payment_method?: string;
  confirm_body?: Post_Confirm;
  reserve_body?: Post_Reserve;
  /**
   * Form-level failure, rendered at the top of the details form. Reserved for
   * problems that belong to the submission rather than to one field — today only
   * the send-code failure. An `updateUser` rejection uses `email_error`.
   */
  update_user_error?: string;
  /**
   * Rejection of the email field itself, rendered in that field's own slot
   * [GRO-6429].
   *
   * Separate from `update_user_error` because every rejection `ApiError` types as
   * `UpdateUserErrorResponse` takes its message from `errors.email[0]` — it is
   * always about this one field, so it renders beside it rather than above the
   * whole form.
   */
  email_error?: string;
  /**
   * Did-you-mean suggestion belonging to `email_error` [GRO-6429].
   *
   * Rides the same channel as the message it explains because the rejection is
   * raised on `../verify` (after the OTP, where `updateUser` runs) but rendered
   * on `../details`, the only page with the email field to correct. Written and
   * cleared together with `email_error` by `widgetUpdateUserRejection()`, so a
   * later rejection carrying no suggestion cannot leave a stale one pointing at
   * an address the guest no longer has.
   */
  email_domain_suggestion?: EmailDomainSuggestion;
  payment_error?: string;
  reservation_error?: string;
  opt_in_accepted?: number;
  aeg_terms_accepted?: boolean;
  /** Event-only: user acknowledged Dorsia Terms of Use + Privacy Policy for the purchase */
  dorsia_terms_accepted?: boolean;
  /** Event-only: user acknowledged the no-refund / all-sales-final policy */
  no_refund_accepted?: boolean;
  ach_payment_id?: string;
  ach_bank_name?: string;
  ach_bank_last4?: string;
  event?: Event;
  eventInfo?: EventInfo;
  referral_code?: string | null;
  alternative_pickup_first_name?: string;
  alternative_pickup_last_name?: string;
  alternative_pickup_phone?: string;
  alternative_pickup_email?: string;
  bookingType?: 'restaurant' | 'event' | 'custom_request';
  custom_request?: {
    date: string;
    party_size: number;
    selected_times: string[];
    selected_table_types: number[];
    selected_table_type_names: string[];
    per_person_spend: number;
    minimum_spend: number;
    expiration_hours?: number;
    expiration_description?: string;
    comments?: string;
  };
  /** Index of the current step in the custom request wizard (persists across refresh) */
  custom_request_step?: number;
  /** Restaurant ID the current custom_request belongs to (detects restaurant switches) */
  custom_request_restaurant_id?: number;
  /** Post-verification redirect path (e.g. "confirmed/uuid" or "request/submitted/uuid") */
  returnTo?: string;
  /**
   * True once the flow reached a booking confirmation. Persisted only in the
   * in-memory embed session (never partner-origin storage); read by
   * `shouldResetEmbedSession` so a reopen after a completed booking starts fresh.
   */
  completed?: boolean;
  /**
   * Whether the guest wants to receive Dorsia marketing SMS. Default `true`
   * (pre-checked in DetailsPage). Passed to `verification()` as
   * `subscribe_to_general_marketing_sms`. Opt-in only, an unchecked box does
   * not unsubscribe existing subscribers.
   */
  marketing_sms_opt_in?: boolean;
}

export interface CardImage {
  id: number;
  original_url: string;
}

export interface EventInfo {
  id?: number;
  ticketItemId?: number;
  quantity?: number;
  fee_restaurant?: number;
  fee_restaurant__currency?: string;
  items?: Items[];
  party_size?: number;
  restaurant?: RestaurantResponseData;
  service_charges?: Array<{ label: string; amount: number; amount__currency: string }>;
  service_charges__currency?: Array<{ label: string; amount: string }>;
  tax_amount?: number;
  tax_amount__currency?: string;
  tax_rate?: number;
  total_paid?: number;
  total_paid__currency?: string;
  points_used_value?: number;
  points_used_currency_value__currency?: string;
  credits_used?: number;
  credits_used__currency?: string;
  allowedPaymentTypes?: Array<PaymentType | string>;
  /** Add-on inventory IDs selected by the user, repeated by quantity */
  addOnItemIds?: number[];
  /** True when the selected ticket's access type requires identity verification. */
  requires_idv?: boolean;
}

export interface Items {
  guests?: number;
  price__currency?: string;
  quantity?: number;
  item?: ItemInfo;
}

export interface ItemInfo {
  name?: string;
  image?: string;
}

export interface Event {
  id: number;
  card_image?: CardImage;
  date?: string;
  datetime_local__formatted?: {
    date?: string;
    date_long?: string;
    date_dow_long?: string;
    date_dow_short?: string;
    time?: string;
  };
  name?: string;
  time?: string;
  time_end?: string;
}

export interface UserDetails {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  referral_code?: string | null;
}

export interface RequestOptionsTime {
  local: string;
  utc: string;
  carbon: string;
  pretty: string;
}

export interface RequestOptionsResponse {
  times: RequestOptionsTime[];
}

export interface CustomRequestSettings {
  price_options: CustomRequestPriceOption[];
  expiration_options: CustomRequestExpirationOption[];
  table_options: CustomRequestTableOption[];
  response_time: {
    average_response_time_in_minutes: number | null;
    average_response_time_as_text: string | null;
    average_response_time_verbiage: string | null;
  };
}

export interface CustomRequestSettingsResponse {
  data: {
    customRequestSettings: CustomRequestSettings;
  };
}

export interface CustomRequestPriceOption {
  price: number;
  acceptance_chance: 'GOOD' | 'FAIR' | 'LOW';
  acceptance_label: string;
  default: boolean;
}

export interface CustomRequestExpirationOption {
  hours: number;
  description: string;
  default: boolean;
}

export interface CustomRequestTableOption {
  id: number;
  name: string;
}

export interface CustomRequestPreviewBody {
  party: number;
  date: string;
  times: string[];
  spend: number;
  table_types?: number[];
  expiration_hours?: number;
}

export interface CustomRequestSubmitBody {
  date: string;
  times: string[];
  party: number;
  minimum_spend: number;
  per_person_spend: number;
  payment_method_id: string;
  table_types?: number[];
  expiration_hours?: number;
  comments?: string;
  referral_code?: string;
}
